import { createQuestionsService } from './questions.service'
import { createUserService } from './user.service'
import { createQuestionGroupsService } from './question-groups.service'
import { createDashboardService } from './dashboard.service'
import { getToken } from '../auth/storage'

const baseUrlApiV1 = '/api/v1'
let unauthorizedHandler = () => {}

export class APIError extends Error {
  constructor (status, message, payload) {
    super(message)
    this.name = 'APIError'
    this.status = status
    this.payload = payload
  }
}

export function setUnauthorizedHandler (handler) {
  unauthorizedHandler = typeof handler === 'function' ? handler : () => {}
}

async function request (url, options = {}, authenticated = true, timeoutMs = 15000) {
  const headers = new Headers(options.headers || {})
  const token = getToken()
  if (authenticated && token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(new DOMException('Request timed out', 'TimeoutError')), timeoutMs)
  let response
  try {
    response = await fetch(url, { ...options, headers, signal: controller.signal })
  } finally {
    clearTimeout(timeout)
  }
  const payload = response.status === 204 ? null : await response.json().catch(() => null)
  if (!response.ok) {
    if (response.status === 401 && authenticated && token === getToken()) {
      unauthorizedHandler()
    }
    const message = payload?.error?.message || `Request failed with status ${response.status}`
    throw new APIError(response.status, message, payload)
  }
  return payload
}

// requestBlob performs an authenticated GET and returns the raw response body
// as a Blob, for downloads (e.g. CSV export) that are not JSON envelopes.
async function requestBlob (url) {
  const headers = new Headers()
  const token = getToken()
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 30000)
  let response
  try {
    response = await fetch(url, { method: 'GET', headers, signal: controller.signal })
  } finally {
    clearTimeout(timeout)
  }
  if (!response.ok) {
    if (response.status === 401 && token === getToken()) {
      unauthorizedHandler()
    }
    throw new APIError(response.status, `Request failed with status ${response.status}`)
  }
  return response.blob()
}

const http = {
  get (url, options = {}) {
    return request(url, { method: 'GET' }, options.authenticated !== false)
  },
  getBlob (url) {
    return requestBlob(url)
  },
  post (url, data, options = {}) {
    return request(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }, options.authenticated !== false, options.timeout)
  },
  put (url, data) {
    return request(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
  },
  delete (url) {
    return request(url, { method: 'DELETE' })
  }
}

export const questionsService = createQuestionsService(http, baseUrlApiV1)
export const userService = createUserService(http, baseUrlApiV1)
export const questionGroupsService = createQuestionGroupsService(http, baseUrlApiV1)
export const dashboardService = createDashboardService(http, baseUrlApiV1)
